import { isCancelledAppointment } from "./cancelledAppointment";
import { useEffect, useMemo, useState } from "react";

import { api } from "../shared/utilities/api";
import apiConfigs from "../shared/utilities/apiConfigs";

// ── Tokens ────────────────────────────────────────────────────
const ORANGE       = "#EA8331";
const ORANGE_DARK  = "#C96820";
const BLUE         = "#2E90FA";
const DANGER       = "#D92D20";
const DANGER_SOFT  = "#FEF3F2";
const TEXT_PRIMARY = "#111827";
const TEXT_SEC     = "#6B7280";
const BORDER       = "rgba(15,23,42,0.08)";

// ─── Lightweight responsive + animation overrides ──────────────
const injectResponsiveStyles = () => {
	if (document.getElementById("patient-appt-responsive")) return;
	const style = document.createElement("style");
	style.id = "patient-appt-responsive";
	style.textContent = `
		.pa-wrap {
			max-width: 100%;
			overflow-x: hidden;
		}
		.pa-header {
			cursor: pointer;
			user-select: none;
		}
		.pa-header:hover .pa-chevron {
			background: #D6E9FF;
		}
		.pa-chevron {
			transition: transform 0.25s ease, background 0.15s ease;
		}
		.pa-chevron.expanded {
			transform: rotate(180deg);
		}
		.pa-body {
			animation: pa-slideDown 0.22s ease;
			overflow: hidden;
		}
		@keyframes pa-slideDown {
			from { opacity: 0; max-height: 0; }
			to { opacity: 1; max-height: 2000px; }
		}
	`;
	document.head.appendChild(style);
};

// ── Helpers ───────────────────────────────────────────────────
const formatAppointmentDateTime = (appointmentDate, appointmentTime) => {
	if (!appointmentDate || !appointmentTime) return null;
	return new Date(`${appointmentDate}T${appointmentTime}`);
};

const getAppointmentTimestamp = (appointment) => {
	const dt = formatAppointmentDateTime(
		appointment?.appointmentDate,
		appointment?.startTime,
	);
	if (!dt || Number.isNaN(dt.getTime())) return Number.NEGATIVE_INFINITY;
	return dt.getTime();
};

const formatDateLabel = (appointmentDate) => {
	if (!appointmentDate) return "Date unavailable";
	const d = new Date(`${appointmentDate}T00:00:00`);
	if (Number.isNaN(d.getTime())) return appointmentDate;
	return d.toLocaleDateString(undefined, {
		weekday: "short",
		month: "short",
		day: "numeric",
		year: "numeric",
	});
};

const formatTimeLabel = (appointmentDate, appointmentTime) => {
	const dt = formatAppointmentDateTime(appointmentDate, appointmentTime);
	if (!dt || Number.isNaN(dt.getTime())) return "Time unavailable";
	return dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

const getDateParts = (appointmentDate) => {
	if (!appointmentDate) return { day: "--", month: "---" };
	const d = new Date(`${appointmentDate}T00:00:00`);
	if (Number.isNaN(d.getTime())) return { day: "--", month: "---" };
	return {
		day: String(d.getDate()).padStart(2, "0"),
		month: d.toLocaleDateString(undefined, { month: "short" }).toUpperCase(),
	};
};

// Mirrors porton-bookapp/client/cancellation.jsx's isWithin24Hours so both
// apps enforce the same cancellation window with identical semantics.
const isWithin24Hours = (appointment) => {
	const dt = formatAppointmentDateTime(appointment?.appointmentDate, appointment?.startTime);
	if (!dt || Number.isNaN(dt.getTime())) return false;
	const diffMs = dt.getTime() - Date.now();
	return diffMs > 0 && diffMs <= 24 * 60 * 60 * 1000;
};

// ── Icons ─────────────────────────────────────────────────────
const CalendarIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<rect x="3" y="4" width="18" height="18" rx="2" />
		<line x1="16" y1="2" x2="16" y2="6" />
		<line x1="8" y1="2" x2="8" y2="6" />
		<line x1="3" y1="10" x2="21" y2="10" />
	</svg>
);

const ChevronIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15" aria-hidden="true">
		<polyline points="6 9 12 15 18 9" />
	</svg>
);

// ── Sub-components ────────────────────────────────────────────

const SectionBadge = ({ count, tone }) => {
	const isUpcoming = tone === "upcoming";
	return (
		<div
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 6,
				padding: "4px 10px",
				borderRadius: 20,
				background: isUpcoming ? "#FFF7F1" : "#F0F7FF",
				border: `0.5px solid ${isUpcoming ? "rgba(234,131,49,.2)" : "rgba(46,144,250,.2)"}`,
				fontSize: 12,
				fontWeight: 500,
				color: isUpcoming ? ORANGE : BLUE,
				fontFamily: "Roboto, sans-serif",
			}}
		>
			<div
				style={{
					width: 7,
					height: 7,
					borderRadius: "50%",
					background: isUpcoming ? ORANGE : BLUE,
					flexShrink: 0,
				}}
			/>
			{count} {count === 1 ? "appointment" : "appointments"}
		</div>
	);
};

// ── Cancel dialog (no MUI) ────────────────────────────────────
const CancelDialog = ({ open, onClose, onConfirm, isCancelling }) => {
	if (!open) return null;
	return (
		<div
			style={{
				position: "fixed",
				inset: 0,
				background: "rgba(17,24,39,0.4)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				zIndex: 1000,
				padding: 16,
			}}
			onClick={isCancelling ? undefined : onClose}
		>
			<div
				style={{
					background: "#FFFFFF",
					borderRadius: 16,
					padding: 24,
					width: "100%",
					maxWidth: 380,
					boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
				}}
				onClick={(e) => e.stopPropagation()}
			>
				<h3
					style={{
						fontSize: 17,
						fontWeight: 500,
						color: TEXT_PRIMARY,
						marginBottom: 14,
						fontFamily: "Roboto, sans-serif",
					}}
				>
					Cancel Appointment
				</h3>

				<div
					style={{
						background: DANGER_SOFT,
						border: "0.5px solid rgba(217,45,32,0.15)",
						borderRadius: 10,
						padding: 14,
						marginBottom: 20,
					}}
				>
					<p
						style={{
							fontSize: 14,
							color: "#475467",
							lineHeight: 1.6,
							fontFamily: "Roboto, sans-serif",
						}}
					>
						Are you sure you want to cancel this appointment?
					</p>
				</div>

				<div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
					<button
						disabled={isCancelling}
						onClick={onClose}
						style={{
							padding: "9px 18px",
							borderRadius: 9,
							border: `0.5px solid ${BORDER}`,
							background: "#FFFFFF",
							color: TEXT_PRIMARY,
							fontSize: 14,
							fontWeight: 500,
							cursor: isCancelling ? "not-allowed" : "pointer",
							fontFamily: "Roboto, sans-serif",
						}}
					>
						Keep
					</button>
					<button
						disabled={isCancelling}
						onClick={onConfirm}
						style={{
							padding: "9px 18px",
							borderRadius: 9,
							border: "none",
							background: DANGER,
							color: "#FFFFFF",
							fontSize: 14,
							fontWeight: 500,
							cursor: isCancelling ? "not-allowed" : "pointer",
							fontFamily: "Roboto, sans-serif",
							opacity: isCancelling ? 0.7 : 1,
						}}
					>
						{isCancelling ? "Cancelling…" : "Confirm Cancel"}
					</button>
				</div>
			</div>
		</div>
	);
};

// ── Timeline row: upcoming ─────────────────────────────────────
const UpcomingTimelineRow = ({ appointment, orgId, orgContactNum, onCancelSuccess, isLast }) => {
	const [openCancel, setOpenCancel]   = useState(false);
	const [isCancelling, setIsCancelling] = useState(false);
	const { day, month } = getDateParts(appointment.appointmentDate);
	const soon = isWithin24Hours(appointment);

	const handleCancel = async () => {
		try {
			setIsCancelling(true);
			await api(
				apiConfigs.baseOption(
					"get",
					`orgs/${orgId}/appointment/${appointment.id}/cancel`,
				),
			);
			setOpenCancel(false);
			if (onCancelSuccess) onCancelSuccess();
			else window.location.reload();
		} catch {
			setOpenCancel(false);
		} finally {
			setIsCancelling(false);
		}
	};

	return (
		<>
			<div style={{ display: "flex", gap: 14, paddingBottom: isLast ? 0 : 16 }}>
				{/* Left: date box + line */}
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						flexShrink: 0,
						width: 44,
					}}
				>
					<div
						style={{
							width: 44,
							height: 44,
							borderRadius: 12,
							background: "#FEF0E4",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						<span
							style={{
								fontSize: 16,
								fontWeight: 500,
								color: ORANGE,
								lineHeight: 1,
								fontFamily: "Roboto, sans-serif",
							}}
						>
							{day}
						</span>
						<span
							style={{
								fontSize: 10,
								fontWeight: 500,
								color: ORANGE,
								textTransform: "uppercase",
								letterSpacing: "0.04em",
								marginTop: 2,
								fontFamily: "Roboto, sans-serif",
							}}
						>
							{month}
						</span>
					</div>
					{!isLast && (
						<div
							style={{
								width: 1.5,
								flex: 1,
								background: BORDER,
								marginTop: 4,
							}}
						/>
					)}
				</div>

				{/* Right: card */}
				<div
					style={{
						flex: 1,
						background: "#FFFFFF",
						border: `0.5px solid ${BORDER}`,
						borderRadius: 12,
						padding: "12px 14px",
						display: "flex",
						flexDirection: "column",
						gap: 10,
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 12,
						}}
					>
						<div>
							<div
								style={{
									fontSize: 14,
									fontWeight: 500,
									color: TEXT_PRIMARY,
									fontFamily: "Roboto, sans-serif",
								}}
							>
								{formatDateLabel(appointment.appointmentDate)}
							</div>
							<div
								style={{
									fontSize: 13,
									color: TEXT_SEC,
									marginTop: 2,
									fontFamily: "Roboto, sans-serif",
								}}
							>
								{formatTimeLabel(appointment.appointmentDate, appointment.startTime)}
							</div>

							{soon && (
								<span
									style={{
										display: "inline-flex",
										alignItems: "center",
										gap: 4,
										marginTop: 6,
										padding: "3px 8px",
										borderRadius: 4,
										background: "#FFF1E9",
										color: "#8A3A12",
										fontSize: 9,
										fontWeight: 600,
										textTransform: "uppercase",
										letterSpacing: "0.05em",
										fontFamily: "Roboto, sans-serif",
									}}
								>
									Within 24 Hours
								</span>
							)}
						</div>

						<button
							onClick={() => setOpenCancel(true)}
							disabled={soon}
							style={{
								padding: "6px 14px",
								borderRadius: 8,
								border: "1px solid rgba(217,45,32,0.2)",
								background: "transparent",
								color: DANGER,
								fontSize: 12,
								fontWeight: 500,
								cursor: soon ? "not-allowed" : "pointer",
								opacity: soon ? 0.4 : 1,
								fontFamily: "Roboto, sans-serif",
								whiteSpace: "nowrap",
								flexShrink: 0,
							}}
							onMouseEnter={(e) => {
								if (!soon) e.currentTarget.style.background = DANGER_SOFT;
							}}
							onMouseLeave={(e) => {
								if (!soon) e.currentTarget.style.background = "transparent";
							}}
						>
							Cancel
						</button>
					</div>

					{soon && (
						<div
							style={{
								display: "flex",
								alignItems: "flex-start",
								gap: 8,
								borderTop: `0.5px solid ${BORDER}`,
								paddingTop: 10,
							}}
						>
							<div>
								<p
									style={{
										fontSize: 11,
										lineHeight: 1.5,
										color: TEXT_SEC,
										fontFamily: "Roboto, sans-serif",
										margin: 0,
									}}
								>
									Online cancellation is unavailable within 24 hours of your
									appointment. Please contact the clinic directly if you need to
									cancel or reschedule your appointment.
								</p>
								{orgContactNum && (
									<a
										href={`tel:${String(orgContactNum).replace(/\D/g, "")}`}
										style={{
											display: "inline-flex",
											alignItems: "center",
											gap: 5,
											marginTop: 6,
											fontSize: 11,
											fontWeight: 500,
											color: ORANGE_DARK,
											textDecoration: "none",
											fontFamily: "Roboto, sans-serif",
										}}
									>
										Call the clinic
									</a>
								)}
							</div>
						</div>
					)}
				</div>
			</div>

			<CancelDialog
				open={openCancel}
				onClose={() => setOpenCancel(false)}
				onConfirm={handleCancel}
				isCancelling={isCancelling}
			/>
		</>
	);
};

// ── Timeline row: past ────────────────────────────────────────
const PastTimelineRow = ({ appointment, isLast }) => {
	const { day, month } = getDateParts(appointment.appointmentDate);

	return (
		<div style={{ display: "flex", gap: 14, paddingBottom: isLast ? 0 : 16 }}>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					flexShrink: 0,
					width: 44,
				}}
			>
				<div
					style={{
						width: 44,
						height: 44,
						borderRadius: 12,
						background: "#EEF5FF",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}
				>
					<span
						style={{
							fontSize: 16,
							fontWeight: 500,
							color: BLUE,
							lineHeight: 1,
							fontFamily: "Roboto, sans-serif",
						}}
					>
						{day}
					</span>
					<span
						style={{
							fontSize: 10,
							fontWeight: 500,
							color: BLUE,
							textTransform: "uppercase",
							letterSpacing: "0.04em",
							marginTop: 2,
							fontFamily: "Roboto, sans-serif",
						}}
					>
						{month}
					</span>
				</div>
				{!isLast && (
					<div style={{ width: 1.5, flex: 1, background: BORDER, marginTop: 4 }} />
				)}
			</div>

			<div
				style={{
					flex: 1,
					background: "#FFFFFF",
					border: `0.5px solid ${BORDER}`,
					borderRadius: 12,
					padding: "12px 14px",
				}}
			>
				<div
					style={{
						fontSize: 14,
						fontWeight: 500,
						color: TEXT_PRIMARY,
						fontFamily: "Roboto, sans-serif",
					}}
				>
					{formatDateLabel(appointment.appointmentDate)}
				</div>
				<div
					style={{
						fontSize: 13,
						color: TEXT_SEC,
						marginTop: 2,
						fontFamily: "Roboto, sans-serif",
					}}
				>
					{formatTimeLabel(appointment.appointmentDate, appointment.startTime)}
				</div>
			</div>
		</div>
	);
};

// ── Section wrapper ───────────────────────────────────────────
const AppointmentSection = ({ title, subtitle, appointments, tone, emptyLabel, orgId, orgContactNum, onCancelSuccess }) => {
	const isUpcoming = tone === "upcoming";

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
			{/* Header */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					flexWrap: "wrap",
					gap: 10,
				}}
			>
				<div>
					<div
						style={{
							fontSize: 15,
							fontWeight: 500,
							color: TEXT_PRIMARY,
							fontFamily: "Roboto, sans-serif",
						}}
					>
						{title}
					</div>
					<div
						style={{
							fontSize: 12,
							color: TEXT_SEC,
							marginTop: 2,
							fontFamily: "Roboto, sans-serif",
						}}
					>
						{subtitle}
					</div>
				</div>
				<SectionBadge count={appointments.length} tone={tone} />
			</div>

			{/* Empty state */}
			{appointments.length === 0 ? (
				<div
					style={{
						border: "1px dashed rgba(15,23,42,0.14)",
						borderRadius: 14,
						padding: 24,
						background: isUpcoming ? "#FFFDFC" : "#FCFCFD",
						textAlign: "center",
						fontSize: 14,
						color: TEXT_SEC,
						fontFamily: "Roboto, sans-serif",
					}}
				>
					{emptyLabel}
				</div>
			) : (
				<div>
					{appointments.map((appt, i) =>
						isUpcoming ? (
							<UpcomingTimelineRow
								key={appt.id}
								appointment={appt}
								orgId={orgId}
								orgContactNum={orgContactNum}
								onCancelSuccess={onCancelSuccess}
								isLast={i === appointments.length - 1}
							/>
						) : (
							<PastTimelineRow
								key={appt.id}
								appointment={appt}
								isLast={i === appointments.length - 1}
							/>
						),
					)}
				</div>
			)}
		</div>
	);
};

// ── Root component ────────────────────────────────────────────
const PatientAppointments = ({ patient, orgId, orgContactNum }) => {
	const [patientAppointments, setPatientAppointments] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);

	useEffect(() => {
		injectResponsiveStyles();
	}, []);

	const fetchPatientAppointments = async (currentOrgId, demographicNo) => {
		setIsLoading(true);
		try {
			const result = await api(
				apiConfigs.baseOption(
					"get",
					`orgs/${currentOrgId}/patient/${demographicNo}/appointments`,
				),
			);
			const fetched = Object.values(result?.data || {});
			setPatientAppointments(fetched.filter((appt) => !isCancelledAppointment(appt)));
		} catch (error) {
			console.error("Error retrieving patient appointments:", error);
			setPatientAppointments([]);
		} finally {
			setIsLoading(false);
		}
	};

	const { pastAppointments, upcomingAppointments } = useMemo(() => {
		const now = new Date();
		const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

		const groups = patientAppointments.reduce(
			(acc, appt) => {
				const dt = formatAppointmentDateTime(appt.appointmentDate, appt.startTime);
				if (dt && !Number.isNaN(dt.getTime()) && dt >= now) {
					acc.upcomingAppointments.push(appt);
				} else if (dt && !Number.isNaN(dt.getTime()) && dt >= thirtyDaysAgo) {
					acc.pastAppointments.push(appt);
				}
				return acc;
			},
			{ upcomingAppointments: [], pastAppointments: [] },
		);

		groups.upcomingAppointments.sort(
			(a, b) => getAppointmentTimestamp(a) - getAppointmentTimestamp(b),
		);
		groups.pastAppointments.sort(
			(a, b) => getAppointmentTimestamp(b) - getAppointmentTimestamp(a),
		);

		return groups;
	}, [patientAppointments]);

	const hasNoAppointments =
		upcomingAppointments.length === 0 && pastAppointments.length === 0;
	const totalCount = upcomingAppointments.length + pastAppointments.length;

	useEffect(() => {
		if (!orgId || !patient?.demographicNo) return;
		fetchPatientAppointments(orgId, patient.demographicNo);
	}, [orgId, patient?.demographicNo]);

	const handleRefreshAfterCancel = () => {
		if (!orgId || !patient?.demographicNo) return;
		fetchPatientAppointments(orgId, patient.demographicNo);
	};

	return (
		<>
			<div
				className="pa-wrap"
				style={{
					background: "#FFFFFF",
					border: "0.5px solid #E5E7EB",
					borderRadius: 16,
					overflow: "hidden",
				}}
			>
			{/* ── Header — clickable to expand/collapse, matches PatientSettings ── */}
			<div
				className="pa-header"
				onClick={() => setIsExpanded((prev) => !prev)}
				style={{
					padding: "20px 20px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 12,
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 12,
						minWidth: 0,
						flexWrap: "wrap",
					}}
				>
					{/* Calendar icon box */}
					<div
						style={{
							width: 38,
							height: 38,
							borderRadius: 10,
							background: "#EEF5FF",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						<CalendarIcon />
					</div>

					<div style={{ minWidth: 0 }}>
						<div
							style={{
								fontSize: 15,
								fontWeight: 500,
								color: TEXT_PRIMARY,
								fontFamily: "Roboto, sans-serif",
							}}
						>
							Appointments
						</div>
						<div
							style={{
								fontSize: 13,
								color: TEXT_SEC,
								marginTop: 2,
								fontFamily: "Roboto, sans-serif",
							}}
						>
							Upcoming and past visits
						</div>

						{/* Current state pill — visible even when collapsed */}
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 5,
								marginTop: 10,
								background: "#F3F4F6",
								border: "0.5px solid #E5E7EB",
								borderRadius: 20,
								padding: "4px 10px 4px 6px",
								maxWidth: "100%",
							}}
						>
							<div
								style={{
									width: 7,
									height: 7,
									borderRadius: "50%",
									background: ORANGE,
									flexShrink: 0,
								}}
							/>
							<span
								style={{
									fontSize: 12,
									color: TEXT_SEC,
									fontFamily: "Roboto, sans-serif",
								}}
							>
								{isLoading
									? "Loading…"
									: `${upcomingAppointments.length} upcoming, ${pastAppointments.length} past`}
							</span>
						</div>
					</div>
				</div>

				{/* Chevron toggle */}
				<div
					className={`pa-chevron${isExpanded ? " expanded" : ""}`}
					style={{
						width: 30,
						height: 30,
						borderRadius: 8,
						background: "#EEF5FF",
						color: BLUE,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}
				>
					<ChevronIcon />
				</div>
			</div>

			{isExpanded && (
				<div className="pa-body">
					{/* ── Divider ── */}
					<div style={{ height: "0.5px", background: "#E5E7EB" }} />

					<div style={{ padding: "20px" }}>
						{isLoading ? (
							<div
								style={{
									border: "1px dashed rgba(15,23,42,0.14)",
									borderRadius: 14,
									padding: 24,
									background: "#FCFCFD",
									textAlign: "center",
									fontSize: 14,
									color: TEXT_SEC,
									fontFamily: "Roboto, sans-serif",
								}}
							>
								Loading appointments…
							</div>
						) : hasNoAppointments ? (
							<div
								style={{
									border: "1px dashed rgba(15,23,42,0.14)",
									borderRadius: 14,
									padding: 24,
									background: "#FCFCFD",
									textAlign: "center",
									fontSize: 14,
									color: TEXT_SEC,
									fontFamily: "Roboto, sans-serif",
								}}
							>
								No appointments available.
							</div>
						) : (
							<div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
								<AppointmentSection
									title="Upcoming Appointments"
									subtitle="Scheduled appointments"
									appointments={upcomingAppointments}
									emptyLabel="No upcoming appointments."
									tone="upcoming"
									orgId={orgId}
									orgContactNum={orgContactNum}
									onCancelSuccess={handleRefreshAfterCancel}
								/>
								<AppointmentSection
									title="Past Appointments"
									subtitle="Appointments from the last 30 days"
									appointments={pastAppointments}
									emptyLabel="No past appointments in the last 30 days."
									tone="past"
									orgId={orgId}
								/>
							</div>
						)}
					</div>
				</div>
			)}
		</div>

		</>
	);
};

export default PatientAppointments;